// Stale times — one place (ARCH §9.3; the T-022 → T-023 Inbox note).
//
// A demo account rebases once a minute (ENGINE §14, `?demoNow=` aside), so its reads are fresh for 60 s. A
// live account's data moves when the backend syncs, i.e. every `limits.refreshMinutes` (API §12.2) — reads
// are fresh for that long. `AccountInfo.limits.refreshMinutes` is `0` on a demo (T-021 note (5)), so the mode
// decides first and the minutes only apply to live accounts.

import type { AccountMode, PlanLimits } from '../types/session';

/** What `staleTimeFor` needs — `AccountInfo` satisfies it as `{ mode, refreshMinutes: limits.refreshMinutes }`. */
export interface Freshness {
  mode: AccountMode;
  refreshMinutes: PlanLimits['refreshMinutes'];
}

export const DEMO_STALE_MS = 60_000;
/** A live account whose plan states no refresh interval (or `0`): the manual-refresh floor of API §2.4. */
export const DEFAULT_LIVE_REFRESH_MINUTES = 5;

export function isDemoMode(mode: AccountMode): boolean {
  return mode === 'demo' || mode === 'demo_view_only_preview';
}

/**
 * Milliseconds a read stays fresh. `freshness` absent (before `account` has loaded) → the transport decides:
 * a demo client is a demo account; an http client gets the live default.
 */
export function staleTimeFor(
  freshness: Freshness | null | undefined,
  transport: 'http' | 'demo' = 'http',
): number {
  if (!freshness) return transport === 'demo' ? DEMO_STALE_MS : liveStaleMs(0);
  if (isDemoMode(freshness.mode)) return DEMO_STALE_MS;
  return liveStaleMs(freshness.refreshMinutes);
}

function liveStaleMs(refreshMinutes: number): number {
  const minutes =
    Number.isFinite(refreshMinutes) && refreshMinutes > 0
      ? refreshMinutes
      : DEFAULT_LIVE_REFRESH_MINUTES;
  return minutes * 60_000;
}

/** `Freshness` straight off an `AccountInfo`-shaped object. */
export function freshnessOf(account: { mode: AccountMode; limits: PlanLimits }): Freshness {
  return { mode: account.mode, refreshMinutes: account.limits.refreshMinutes };
}
